<template>
  <div class="container">
    <section>
      <div v-if="newStudy" class="layout">
        <div class="child">
          <div class="pure-g">
            <div class="pure-u-1">
              <div>
                <h2>{{ headerText }}</h2>
              </div>
              <div>
                <h3>Press Preview to see what your study will look like on the web.</h3>
              </div>
              <div>
                <router-link :to="sendToPreview" target="_blank">
                  <button class="pure-button bigmi-wide-button bigmi-cyan-blue-2-button">PREVIEW
                  </button>
                </router-link>
              </div>
              <form class="pure-form pure-form-stacked" @submit.prevent="handleSubmit" novalidate>
                <h3>Just before you finalize the study and publish:</h3>
                <div class="description" style="padding-top: 15px;">
                  {{ methodDescription }}
                </div>
                <div>
                  <h3>
                    How many respondents do you want?
                    (Between between {{ participantsRangeMin }} and {{ participantsRangeMax | number }}
                    - you can add more respondents later.)
                  </h3>
                </div>
                <div class="pdng-1 vertical">
                  <label>
                    <input type="text" placeholder="Number of respondents" v-model="formModel.n" autofocus>
                  </label>
                  <div v-if="ifShowErrorMessage" style="color: red; font-size: 12px">
                    * Need between 4 and 9999 respondents
                  </div>
                </div>
                <div>
                  <h3>
                    Would you like to privatize the data of this study? The fee to privatize the data is an additional $2 per respondent.
                  </h3>
                </div>
                <fieldset>
                  <div class="regular radio-row">
                    <div class="bigmi-checkbox">
                      <div>
                        <input id="regular" type="radio" value="private" v-model="selectedValue">
                      </div>
                      <div>
                        <label for="regular">
                          <span class="label-text">Yes - I want my data to remain private and obtain all the rights to the data</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="student radio-row">
                    <div class="bigmi-checkbox">
                      <div>
                        <input id="student" type="radio" value="public" v-model="selectedValue">
                      </div>
                      <div>
                        <label for="student">
                          <span class="label-text">No - The data from my study can remain public and BimiLeap reserves the right to sell or use the data for any purpose</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div v-if="submitTouched && selectedValue === ''" style="color: red">
                    * One of the above selections is required
                  </div>
                </fieldset>
                <div v-if="showCreditCardInfo" class="pdng-1 vertical description">
                  <div class="cost-breakdown">
                    <div v-if="isSourcingLucid" class="line-item">Sample Processing Cost ($4/respondent)</div>
                    <div v-if="isSourcingLucid" class="price">{{ priceForRespondentsSampleFee | currency }}</div>
                    <div v-if="isSourcingSelf" class="line-item">Respondent Processing Fee ($2/respondent)</div>
                    <div v-if="isSourcingSelf" class="price">{{ priceForRespondentsProcessingFee | currency }}</div>
                    <div v-if="isPrivatized" class="line-item">Privatization Processing Fee ($2/respondent)</div>
                    <div v-if="isPrivatized" class="price">{{ priceForRespondentsPrivatizationFee | currency }}</div>
                    <div style="border-bottom: 2px solid black"></div>
                    <div style="border-bottom: 2px solid black"></div>
                    <div class="line-item">Total</div>
                    <div class="price">{{ totalPrice | currency }}</div>
                  </div>
                </div>
                <div v-show="showCreditCardInfo">
                  <payment :ref="paymentComponentRefName"></payment>
                </div>
                <div v-if="!showCreditCardInfo" style="padding: 8px 0 16px 0;">
                  <span>
                    You’re all set! Proceed to publish your study!
                  </span>
                </div>
                <div style="border-bottom: 1px solid #cacaca"></div>
                <div class="agree-to-terms-row">
                  <div class="bigmi-checkbox">
                    <div>
                      <input id="agree-new" type="checkbox" v-model="formModel.agreed">
                    </div>
                    <div>
                      <label for="agree-new">
                          <span>I agree to the
                            <router-link :to="termsOfServiceLink" target="_blank">
                              Terms of Service.
                            </router-link>
                          </span>
                      </label>
                      <label>
                        <span class="error" v-show="!formModel.agreed && submitTouched">*Required Field</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div class="pdng-1 vertical button-row-bottom">
                  <div class="publish">
                    <button type="submit" class="pure-button bigmi-wide-button bigmi-red-2-button">
                      <Fai v-if="isStudyUpdating" :icon="['far', 'circle-notch']" spin/>
                      <span v-else>{{ buttonText }}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="updatingStudy" class="layout">
        <div class="child">
          <form class="pure-form pure-form-stacked" @submit.prevent="handleSubmit" novalidate>
            <div class="pure-g">
              <div class="pure-u-1">
                <div>
                  <h2>{{ headerText }}</h2>
                </div>
                <div v-if="updatingStudy" class="updating-participants-input">
                  <div class="row-header">Current Participants</div>
                  <div class="row-value">{{ maxParts }}</div>
                  <div class="row-header">Additional Participants</div>
                  <div class="row-value">
                    <input type="text" placeholder="Number of respondents" v-model="formModel.n" autofocus
                           style="text-align: center">
                  </div>
                  <div v-if="ifShowErrorMessage">
                  </div>
                  <div v-if="ifShowErrorMessage" style="color: red; font-size: 12px; position: relative; top: -10px;">
                    * Need between 4 and 9999 respondents
                  </div>
                  <div class="row-header">Total Participants</div>
                  <div class="row-value">{{ maxParts + nAsNumber }}</div>
                </div>
                <div v-if="showCreditCardInfo" class="pdng-1 vertical description">
                  <div class="cost-breakdown">
                    <div v-if="isSourcingLucid" class="line-item">Sample Processing Cost ($4/respondent)</div>
                    <div v-if="isSourcingLucid" class="price">{{ priceForRespondentsSampleFee | currency }}</div>
                    <div v-if="isSourcingSelf" class="line-item">Respondent Processing Fee ($2/respondent)</div>
                    <div v-if="isSourcingSelf" class="price">{{ priceForRespondentsProcessingFee | currency }}</div>
                    <div v-if="isPrivatized" class="line-item">Privatization Processing Fee ($2/respondent)</div>
                    <div v-if="isPrivatized" class="price">{{ priceForRespondentsPrivatizationFee | currency }}</div>
                    <div style="border-bottom: 2px solid black"></div>
                    <div style="border-bottom: 2px solid black"></div>
                    <div class="line-item">Total</div>
                    <div class="price">{{ totalPrice | currency }}</div>
                  </div>
                </div>
                <div v-show="showCreditCardInfo">
                  <payment :ref="paymentComponentRefName"></payment>
                </div>
                <div v-if="!showCreditCardInfo">
                  <span>
                    You’re all set! Proceed to add respondents!
                  </span>
                </div>
                <div class="pdng-1 vertical button-row-bottom">
                  <div v-if="updatingStudy" class="cancel">
                    <button v-if="closeButtonPristine" class="pure-button bigmi-wide-button"
                            v-on:click.prevent="cancelAddingParticipants">
                      <span>CANCEL</span>
                    </button>
                    <Loading v-else-if="closeButtonLoading"></Loading>
                  </div>
                  <div class="publish">
                    <button type="submit" class="pure-button bigmi-wide-button bigmi-red-2-button">
                      <Fai v-if="isStudyUpdating" :icon="['far', 'circle-notch']" spin/>
                      <span v-else>{{ buttonText }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
    <div v-if="modalShowState">
      <Modal>
        <div class="confirm-delete-study-dialog pdng-1">
          <div>
            <div class="exit-row">
              <Fai :icon="['fal', 'times']" @click="closeOrProceed()"/>
            </div>
            <div class="pdng-1">
              <div class="prompt-row">
                <span v-if="modalLoadedSuccessfully">{{ modalSuccessMessage }}</span>
                <span
                  v-else-if="modalFailedToLoad">Unable to complete purchase, please check input and try again.</span>
                <span v-else-if="modalStillLoading">{{ modalLoadingStatement }}</span>
              </div>
              <div class="actions-row pdng-1 top">
                <div class="action">
                  <span v-if="modalStillLoading">
                      <Loading></Loading>
                  </span>
                  <button v-else type="button"
                          class="pure-button bigmi-wide-button bigmi-cyan-blue-2-button"
                          @click="closeOrProceed()">
                    <span v-if="modalLoadedSuccessfully">{{ modalSuccessButtonText }}</span>
                    <span v-else-if="modalFailedToLoad">BACK</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Payment from '../views/Payment'
import {
  GET_BACKEND_URL,
  GET_HTTP_CONFIG,
  GET_IS_IN_PROGRESS_STUDY_UPDATING,
  GET_NEXT_STUDY_STEP_ROUTE,
  GET_STUDY,
  INSTANTIATE_STRIPE,
  LOAD_STUDIES_OF_USER,
  LOADING_COMPLETED_FAILURE,
  LOADING_COMPLETED_SUCCESS,
  LOADING_PRISTINE,
  LOADING_STARTED,
  PROCESS_PRIVATE_STUDY_PAYMENT,
  R_NAME_MANAGE_STUDY_SURVEY_QUALIFICATIONS_LANGUAGE,
  R_NAME_PRIVACY,
  R_NAME_RESULTS,
  R_NAME_SURVEY_PREVIEW,
  RADIO_BUTTON_VALUE_STANDARD_SERVICE,
  RESET_SELECTED_QUALIFICATIONS,
  RESET_SURVEY_QUALIFICATIONS,
  SEND_LUCID_OBJECT_TO_BACKEND,
  SEND_LUCID_OBJECT_TO_BACKEND_UPDATE,
  SEND_PAYMENT_INFO_TO_BACKEND,
  SET_IN_PROGRESS_STUDY_AGREED_TO_TERMS_FINAL,
  SET_IN_PROGRESS_STUDY_SERVICE_TYPE,
  SET_STUDY_MAX_PARTICIPANTS, SET_STUDY_PRIVATE,
  SET_STUDY_PUBLISHED,
  SET_SURVEY_SELECTED_QUALIFICATIONS,
  SET_TELL_US_WHO_YOU_ARE,
  STUDY_SOURCE_SELECTION_CUSTOM,
  STUDY_SOURCE_SELECTION_EXTERNAL,
  STUDY_SOURCE_SELECTION_LUCID, STUDY_SOURCE_SELECTION_SELF,
  UPDATE_STUDY,
} from '@/services/constants'
import http from "../../services/axios"

const PAYMENT_COMPONENT_REF_NAME = 'payment-ref'
export default {
  name: 'DoneCreatingStudy',
  data() {
    return {
      formModel: null,
      submitTouched: false,
      modalShowState: false,
      modalStatus: LOADING_PRISTINE,
      closeButtonStatus: LOADING_PRISTINE,
      selectedValue: '',
      couponCheckbox: false,
      maxParts: 0,
      showErrorMessage: false,
      isNewStudy: true
    }
  },
  components: {
    'payment': Payment
  },
  computed: {
    ...mapGetters([
      GET_NEXT_STUDY_STEP_ROUTE,
      GET_IS_IN_PROGRESS_STUDY_UPDATING,
    ]),
    paymentComponentRefName() {
      return PAYMENT_COMPONENT_REF_NAME
    },
    nAsNumber() {
      return Number(this.formModel.n)
    },
    participantsRangeMin() {
      return 4
    },
    participantsRangeMax() {
      return 9999
    },
    isAgreedValid() {
      return this.formModel.agreed
    },
    isFormModelValid() {
      return (this.isAgreedValid || this.updatingStudy) && (this.selectedValue !== '' || this.updatingStudy) && ((this.formModel.n >= this.participantsRangeMin && this.formModel.n <= this.participantsRangeMax))
    },
    sendToPreview() {
      return {
        name: R_NAME_SURVEY_PREVIEW,
        params: {
          study_id: this.$route.params.id
        }
      }
    },
    isPrivatized() {
      return this.newStudy ? this.selectedValue === 'private' : this.$store.state.dataModule.inProgressStudy.hidden
    },
    isStudyUpdating() {
      return this[GET_IS_IN_PROGRESS_STUDY_UPDATING]
    },
    termsOfServiceLink() {
      return {
        name: R_NAME_PRIVACY
      }
    },
    modalLoadedSuccessfully() {
      return this.modalStatus === LOADING_COMPLETED_SUCCESS
    },
    modalFailedToLoad() {
      return this.modalStatus === LOADING_COMPLETED_FAILURE
    },
    modalStillLoading() {
      return this.modalStatus === LOADING_STARTED
    },
    currentNumberOfMaxParticipants() {
      return this.$store.state.dataModule.inProgressStudy.maxParticipants
    },
    totalNumberOfMaxParticipants() {
      return this.currentNumberOfMaxParticipants === -1 ? this.nAsNumber : this.currentNumberOfMaxParticipants + this.nAsNumber
    },
    updatingStudy() {
      return !this.newStudy
    },
    newStudy() {
      return this.isNewStudy
    },
    priceForRespondentsPrivatizationFee() {
      return this.isPrivatized ? this.nAsNumber * 2 : 0
    },
    priceForRespondentsProcessingFee() {
      return this.isSourcingSelf ? this.nAsNumber * 2 : 0
    },
    priceForRespondentsSampleFee() {
      return this.nAsNumber * 4
    },
    totalPrice() {
      return this.updatingStudy ? this.updatingStudyTotalPrice : this.newStudyTotalPrice
    },
    newStudyTotalPrice() {
      let respondentFee = this.priceForRespondentsSampleFee
      respondentFee = this.isSourcingLucid ? respondentFee : 0
      return respondentFee + this.priceForRespondentsProcessingFee + this.priceForRespondentsPrivatizationFee
    },
    updatingStudyTotalPrice() {
      let respondentFee = this.priceForRespondentsSampleFee
      respondentFee = this.isSourcingLucid ? respondentFee : 0
      return respondentFee + this.priceForRespondentsProcessingFee + this.priceForRespondentsPrivatizationFee
    },
    modalSuccessMessage() {
      return this.totalPrice > 0 ? "Thank you for your purchase." : "Your study has been published";
    },
    modalSuccessButtonText() {
      return "CONTINUE"
    },
    modalLoadingStatement() {
      return this.totalPrice > 0 ? "Please wait while we process your card" : "Please wait";
    },
    showCreditCardInfo() {
      return this.totalPrice > 0
    },
    closeButtonPristine() {
      return this.closeButtonStatus === LOADING_PRISTINE
    },
    closeButtonLoading() {
      return this.closeButtonStatus === LOADING_STARTED
    },
    headerText() {
      return this.updatingStudy ? "ADD PARTICIPANTS" : "DONE!"
    },
    buttonText() {
      return this.updatingStudy ? "ADD PARTICIPANTS!" : "PUBLISH!"
    },
    ifShowErrorMessage() {
      return this.showErrorMessage
    },
    paymentPayload() {
      return {
        token: this.$store.state.paymentModule.privatePaymentPostToStripe.data,
        participants: this.nAsNumber,
        platform: this.priceForRespondentsPrivatizationFee * 100,
        processing: this.priceForRespondentsProcessingFee * 100,
        sample: 0
      }
    },
    lucidPaymentPayload() {
      return Object.assign({}, this.paymentPayload, {sample: this.priceForRespondentsSampleFee * 100})
    },
    studyId() {
      return this.$store.state.dataModule.inProgressStudy.meta.id
    },
    isSourcingLucid() {
      return this.$store.state.dataModule.inProgressStudy.sourcingMethod ===  STUDY_SOURCE_SELECTION_LUCID
    },
    isSourcingCustom() {
      return this.$store.state.dataModule.inProgressStudy.sourcingMethod ===  STUDY_SOURCE_SELECTION_CUSTOM
    },
    isSourcingExternal() {
      return this.$store.state.dataModule.inProgressStudy.sourcingMethod ===  STUDY_SOURCE_SELECTION_EXTERNAL
    },
    isSourcingSelf() {
      return this.$store.state.dataModule.inProgressStudy.sourcingMethod ===  STUDY_SOURCE_SELECTION_SELF
    },
    methodDescription() {
      if (this.isSourcingLucid) {
        return 'You have chosen to have BimiLeap source respondents for this study. The cost is $4/respondent.';
      }
      else if (this.isSourcingCustom) {
        return 'You have chosen to receive a custom sampling of respondents for this study. There is nothing to pay now for this, you will be invoiced outside of the BimiLeap platform.';
      }
      else if (this.isSourcingExternal) {
        return 'You have chosen to receive a respondents for this study through a third party service. There is nothing to pay now for this, you will be invoiced outside of the BimiLeap platform.';
      }
      return 'You have chosen to source respondents for this study by yourself. The processing fee for this study is $2.00/respondent.';
    },
    buildUpdateObject() {
      return {
        "quota": Number(this.nAsNumber),
        "studyID": this.studyId
      }
    },
    ifNeedToRefillLucidQualifications() {
      return (this.newStudy && this.isSourcingLucid && this.$store.state.dataModule.qualifications.loadingState !== LOADING_COMPLETED_SUCCESS)
    },
  },
  methods: {
    handleSubmit() {
      this.submitTouched = true
      if (this.isFormModelValid) {
        this.modalLoading()
        this.isSourcingLucid ? this.lucidPaymentProcess() : this.paymentProcess()
      } else {
        if ((this.formModel.n < this.participantsRangeMin || this.formModel.n > this.participantsRangeMax)) {
          this.showErrorMessage = true
        } else if ((this.formModel.n >= this.participantsRangeMin && this.formModel.n <= this.participantsRangeMax)) {
          this.showErrorMessage = false
        }
      }
    },
    paymentProcess() {
      const card = this.$refs[PAYMENT_COMPONENT_REF_NAME].cardRef
      this.$store.dispatch(PROCESS_PRIVATE_STUDY_PAYMENT, card)
        .then(() => {
          return this.$store.dispatch(SEND_PAYMENT_INFO_TO_BACKEND, {payload: this.paymentPayload, studyId: this.studyId})
        })
        .then(() => {
          if (this.$store.state.paymentModule.privatePaymentPostToBackend.loadingState === LOADING_COMPLETED_SUCCESS) {
            this.modalSucceeded()
          } else {
            this.modalFailure()
          }
        })
        .catch(err => {
          this.modalFailure()
        })
    },
    lucidPaymentProcess(){
      const card = this.$refs[PAYMENT_COMPONENT_REF_NAME].cardRef
      this.$store.dispatch(PROCESS_PRIVATE_STUDY_PAYMENT, card)
        .then(() => {
          return this.$store.dispatch(SEND_PAYMENT_INFO_TO_BACKEND, {payload: this.lucidPaymentPayload, studyId: this.studyId})
        })
        .then(() => {
          if(this.$store.state.paymentModule.privatePaymentPostToBackend.loadingState === LOADING_COMPLETED_SUCCESS) {
            this.$store.commit(SET_SURVEY_SELECTED_QUALIFICATIONS, this.buildUpdateObject)
            let qualifications = this.$store.state.dataModule.selectedQualifications
            let action = this.$route.query.update ? SEND_LUCID_OBJECT_TO_BACKEND_UPDATE : SEND_LUCID_OBJECT_TO_BACKEND
            return this.$store.dispatch(action, qualifications)
          }
          else {
            throw new Error('Error')
          }
        })
        .then(() => {
          if (this.$store.state.paymentModule.publicPaymentPostToBackend.loadingState === LOADING_COMPLETED_SUCCESS) {
            this.modalSucceeded()
          } else {
            this.modalFailure()
          }
        })
        .catch(err => {
          this.modalFailure()
        })
    },
    resetFormModel() {
      const agreedFromStore = this.$store.state.dataModule.inProgressStudy.agreeToTermsFinal
      const formObj = {
        agreed: agreedFromStore,
        n: null
      }
      this.formModel = formObj
    },
    proceedToNextPage() {
      this.closeModal()
      this.$router.push(this[GET_NEXT_STUDY_STEP_ROUTE])
    },
    closeOrProceed() {
      this.modalLoadedSuccessfully ? this.proceedToNextPage() : this.closeModal()
    },
    closeModal() {
      this.modalShowState = false
    },
    setStudyDetails() {
      this.$store.commit(SET_STUDY_MAX_PARTICIPANTS, this.totalNumberOfMaxParticipants)
      this.$store.commit(SET_IN_PROGRESS_STUDY_SERVICE_TYPE, RADIO_BUTTON_VALUE_STANDARD_SERVICE)
      this.$store.commit(SET_IN_PROGRESS_STUDY_AGREED_TO_TERMS_FINAL, this.formModel.agreed)
      this.$store.commit(SET_STUDY_PRIVATE, this.isPrivatized)
      this.$store.commit(SET_STUDY_PUBLISHED, true)
      this.$store.commit(SET_TELL_US_WHO_YOU_ARE, '')
    },
    modalSucceeded() {
      this.setStudyDetails()
      this.$store.dispatch(UPDATE_STUDY).then(() => {
        this.modalStatus = LOADING_COMPLETED_SUCCESS
        this.modalShowState = true
      })
    },
    modalFailure() {
      this.modalStatus = LOADING_COMPLETED_FAILURE
      this.modalShowState = true
    },
    modalLoading() {
      this.modalStatus = LOADING_STARTED
      this.modalShowState = true
    },
    cancelAddingParticipants() {
      if (this.$route.query.manage) {
        this.goToResultsPage()
      } else {
        this.closeStudy()
      }
    },
    goToResultsPage() {
      let studyID = this.$route.params.id
      this.$router.push({name: R_NAME_RESULTS, params: {id: studyID}})
    },
    closeStudy() {
      this.closeButtonStatus = LOADING_STARTED
      let studyID = this.$route.params.id
      http.post(this.$store.getters[GET_BACKEND_URL](`studies/${studyID}/close`), {}, this.$store.getters[GET_HTTP_CONFIG])
        .then(() => {
          return this.$store.dispatch(GET_STUDY, studyID)
        })
        .then(() => {
          return this.$store.dispatch(RESET_SELECTED_QUALIFICATIONS)
        })
        .then(() => {
          this.$store.dispatch(LOAD_STUDIES_OF_USER)
          this.closeButtonStatus = LOADING_PRISTINE
          this.$router.push({name: R_NAME_RESULTS, params: {id: studyID}})
        })
        .catch(err => {
          console.error(err)
        })
    },
    sendToLucidQualifications() {
      this.$router.push({
        name: R_NAME_MANAGE_STUDY_SURVEY_QUALIFICATIONS_LANGUAGE,
        query: this.$route.query
      })
    }
  },
  created() {
    this.resetFormModel()
    this.maxParts = this.currentNumberOfMaxParticipants
    this.isNewStudy = !this.$store.state.dataModule.inProgressStudy.published
    if(this.ifNeedToRefillLucidQualifications){
      this.sendToLucidQualifications()
    }
    this.$store.dispatch(INSTANTIATE_STRIPE)
    this.$store.commit(RESET_SURVEY_QUALIFICATIONS)
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../scss/main";

.container {
  height: 100%;
}

section {
  height: 100%;
}

.error {
  color: red;
}

.layout {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
}

.child {
  width: 100%;
  max-width: 500px;
  margin: 1em;
}

h2 {
  text-align: center;
  color: $red-2;
}

h3 {
  color: $cyan-blue-3;
}

.cost-breakdown {
  display: grid;
  grid-template-columns: 80fr 20fr;
  gap: 20px 0;
  font-size: 1em;
  border: 1px solid gray;
  border-radius: 10px;
  padding: 15px;

  .line-item {
    justify-self: start;
    width: 100%;

    input {
      width: 100%;
      box-shadow: unset;
      border: 0;
      outline: 0;
      background: transparent;
      border-bottom: 1px solid gray;
      border-radius: 0;
    }
  }

  span {
    font-size: 14px;
  }

  .code {
    font-size: 14px;
    color: gray;
  }

  .price {
    justify-self: end;
  }
}

span {
  font-size: 1.17em;
}

.pure-form input[type="text"] {
  background: none;
  box-shadow: none;
  border: none;
  border-radius: 0;
  border-bottom: solid 1px $cyan-blue-4;
  padding: 0.5em 0.2em;
  width: 100%;
  color: $cyan-blue-3;

  &:placeholder-shown {
    color: $gray-3;
  }

  &:-ms-input-placeholder {
    color: $gray-3;
  }
}

.button-row-bottom {
  display: flex;
  align-items: center;
  min-height: 49px;

  .cancel {
    flex-basis: 50%;
  }

  .publish {
    flex-grow: 1;
  }
}

.agree-to-terms-row {
  margin: 1em 0 1em 0;
}

.is-student-row {
  margin: 1em 0 0 0;
}

.navigation-row {
  margin: 1em 0 0 0;
}

.description {
  color: $cyan-blue-3;
  font-size: 16px;
}

.confirm-delete-study-dialog {
  background-color: $white-1;
  border-radius: 5px;

  .exit-row {
    display: flex;
    justify-content: flex-end;
  }

  .actions-row {
    display: flex;
    justify-content: center;

    .action {

    }
  }
}

.button-row {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.updating-participants-input {
  padding: 1em 0;
  display: grid;
  grid-template-columns: 40% 60%;

  .row-header {
    display: flex;
    align-items: center;
    padding: 0.5em 0;
    font-size: 18px;
  }

  .row-value {
    display: flex;
    justify-content: center;
    padding: 0.5em 0;
    font-size: 18px;
  }
}

.primary-color {
  color: $red-2;
}

.bigmi-radio-group {
  display: flex;
  flex-direction: column;

  .radio-row {
    margin: 0;

    &:not(.radio-row-last) {
    }
  }

  .radio-container {
    display: flex;
    padding: .5em;
  }

  label {
    margin: 0;
  }
}

</style>
